import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import {
  priceImprovementResponseAcceptablePriceAdjustmentChanged,
  priceImprovementResponseCommentChanged,
  priceImprovementResponseErrorDismissed,
} from 'state/quotes/priceEnhancements/priceImprovementResponse/priceImprovementResponse.actions';
import {
  getPriceImprovementAcceptablePriceAdjustment,
  getPriceImprovementComment,
  getPriceImprovementRejectionError,
  getPriceImprovementValidationError,
} from 'state/quotes/priceEnhancements/priceImprovementResponse/priceImprovementResponse.selectors';
import {
  priceImprovementRejectedThunk,
  priceImprovementValidatedThunk,
} from 'state/quotes/priceEnhancements/priceImprovementResponse/priceImprovementResponse.thunk';
import { getIsTraderUser, priceImprovementBlotter } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { ApiError } from 'utils/http/apiErrorHelper';

export type PriceImprovementResponseProps = OwnProps & StateProps & DispatchProps;

export interface OwnProps {
  id: string;
  quoteId: string;
}

export interface StateProps {
  isDisabled?: boolean;
  targetAdjustedPrice: number;
  comment?: string;
  acceptablePriceAdjustment?: number;
  isTrader?: boolean;
  validationError?: ApiError;
  rejectionError?: ApiError;
}

export interface DispatchProps {
  onRejected: () => void;
  onValidated: () => void;
  onCommentChanged: (comment?: string) => void;
  onAcceptablePriceAdjustmentChanged: (price?: number) => void;
  onErrorDismissed: () => void;
}

export const mapStateToProps = (state: AppState, { id }: OwnProps): StateProps => {
  const blotterItem = priceImprovementBlotter.getDetailItem(state)!;

  const priceNegotiationAction = blotterItem?.details.actions.find(action => action.type === 'PriceNegotiation');

  const isDisabled = priceNegotiationAction ? priceNegotiationAction.isDisabled : true;

  const targetAdjustedPrice = blotterItem.targetTradingPrice - blotterItem.tradingPrice;
  const comment = getPriceImprovementComment(state, id);
  const acceptablePriceAdjustment = getPriceImprovementAcceptablePriceAdjustment(state, id);
  const isTrader = getIsTraderUser(state);
  const validationError = getPriceImprovementValidationError(state, id);
  const rejectionError = getPriceImprovementRejectionError(state, id);
  return {
    isDisabled,
    targetAdjustedPrice,
    comment,
    acceptablePriceAdjustment,
    isTrader,
    validationError,
    rejectionError,
  };
};

export const mapDispatchToProps = (dispatch: ThunkDispatch, { id, quoteId }: OwnProps): DispatchProps => {
  return {
    onRejected() {
      dispatch(priceImprovementRejectedThunk(id, quoteId));
      logAnalytics.trackEvent('Blotter My price improvements', 'Reject');
    },
    onValidated() {
      dispatch(priceImprovementValidatedThunk(id, quoteId));
      logAnalytics.trackEvent('Blotter My price improvements', 'Validate');
    },
    onCommentChanged(comment) {
      dispatch(priceImprovementResponseCommentChanged(id, comment));
      logAnalytics.trackEvent('Blotter My price improvements', 'Comment');
    },
    onAcceptablePriceAdjustmentChanged(acceptablePriceAdjustment?: number) {
      dispatch(priceImprovementResponseAcceptablePriceAdjustmentChanged(id, acceptablePriceAdjustment));
      logAnalytics.trackEvent('Blotter My price improvements', 'Adjust price');
    },
    onErrorDismissed() {
      dispatch(priceImprovementResponseErrorDismissed(id));
      logAnalytics.trackEvent('Blotter My price improvements', 'Dismiss error');
    },
  };
};

export const connectPriceImprovementResponse = connect(mapStateToProps, mapDispatchToProps);
